/**
 * HomeV2 — startsidesversion på /home-2
 *
 * All markup är kopierad från LandingPageV3 och justerad enligt spec,
 * med fyra nya sektioner:
 *   - SocialProofLogos
 *   - VideoShowcase (video-thumbnail + "kommer snart"-modal)
 *   - CustomerStoriesV2 (utökad till 3 stories)
 *   - TestimonialBlock
 *
 * Coral (#E8634A / v3-coral) används endast på interaktiva element och
 * varumärkesnamnet "LOMOOLO" i hero. Aldrig på rubriker eller dekorativa ikoner.
 */

import { useState, useEffect, useRef, lazy, Suspense, type ReactNode } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  HelpCircle,
  Clock,
  Ghost,
  CheckCircle2,
  Building2,
  TrendingUp,
  Zap,
  ArrowRight,
  Menu,
  X,
  Facebook,
  Instagram,
  Linkedin,
  Search,
  Play,
  Video,
  Quote,
  LogIn,
} from "lucide-react";
import { LomooloLogo } from "@/components/ui/LomooloLogo";
import V3Footer from "@/components/layout/V3Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ui/ScrollReveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
// framer-motion borttagen ur initial-bundlen — ScrollReveal är CSS-only och
// HeroAnimation lazy-laddar framer separat när användaren scrollar dit.
import HeroMockups from "@/components/home-v2/HeroMockups";
// HeroAnimation är tung (~1000 rader, framer-motion-tunga states) och ligger
// under fold — lazy-load så main-bundle blir mindre på mobil.
const HeroAnimation = lazy(() => import("@/components/home-v2/HeroAnimation"));


/* ─── Feature flags ─── */
const SHOW_SOCIAL_PROOF_LOGOS = true;
const SHOW_TESTIMONIALS = true;

/* ─── Shared helpers ─── */
const SectionLabel = ({ children }: { children: string }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[2px] text-v3-coral mb-4 font-dm">{children}</p>
);

const CoralButton = ({
  children,
  to,
  large,
  onClick,
}: {
  children: ReactNode;
  to?: string;
  large?: boolean;
  onClick?: () => void;
}) => {
  const cls = `inline-flex items-center gap-2 rounded-[10px] font-dm font-semibold text-[15px] text-white bg-v3-coral hover:bg-v3-coral-hover transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-coral focus-visible:ring-offset-2 ${large ? "px-10 py-4" : "px-8 py-3.5"}`;
  if (to)
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  return (
    <button onClick={onClick} className={cls}>
      {children}
    </button>
  );
};

const OutlineButton = ({ children, onClick }: { children: ReactNode; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-[10px] font-dm font-semibold text-[15px] text-v3-heading border border-v3-heading/20 bg-transparent hover:bg-v3-heading/5 transition-all px-8 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-heading focus-visible:ring-offset-2"
  >
    {children}
  </button>
);

/* ─── COMPONENT ─── */

/* ─── COMPONENT ─── */
const HomeV2 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const heroSentinelRef = useRef<HTMLDivElement | null>(null);

  // Aktivera den fasta "scrolled"-headern först när hero-sektionens slut
  // har lämnat viewporten ovanför 64px-nav-höjden.
  useEffect(() => {
    const sentinel = heroSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const aboveViewport = entry.boundingClientRect.top < 0;
        setScrolled(!entry.isIntersecting && aboveViewport);
      },
      { rootMargin: "-64px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Stäng mobilmenyn om vi växlar mellan default- och scrolled-header.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [scrolled]);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="home-poppins min-h-screen font-dm text-v3-body overflow-x-hidden"
      role="document"
      style={{ background: "var(--color-v3-bg-primary)" }}
    >
      <SEO
        title="Lomoolo Social — Sociala medier på autopilot med AI"
        description="Lomoolo Social hjälper svenska småföretag att planera, skapa och publicera innehåll till Facebook, Instagram och LinkedIn. AI hela vägen — du bestämmer alltid vad som går ut."
        canonical="https://lomoolo.com/"
        ogTitle="Lomoolo Social — Sociala medier på autopilot med AI"
        ogDescription="AI-driven innehållsplanering för småföretag. Planera, skapa och publicera till Facebook, Instagram och LinkedIn — på en eftermiddag."
        ogUrl="https://lomoolo.com/"
        ogType="website"
        ogImage="https://lomoolo.com/og-image.png"
        twitterTitle="Lomoolo Social — Sociala medier på autopilot med AI"
        twitterDescription="AI-driven innehållsplanering för småföretag. Planera, skapa och publicera till Facebook, Instagram och LinkedIn."
        twitterImage="https://lomoolo.com/og-image.png"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Lomoolo Social",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "AI-drivet verktyg för sociala medier som hjälper svenska småföretag att planera, skapa och publicera innehåll till Facebook, Instagram och LinkedIn.",
            "url": "https://lomoolo.com",
            "inLanguage": "sv-SE",
            "offers": { "@type": "AggregateOffer", "priceCurrency": "SEK", "lowPrice": "599", "highPrice": "1199", "offerCount": "3" },
            "featureList": [
              "AI-genererad företagsprofil från domän",
              "Automatisk årsplanering för innehåll (52 veckor)",
              "AI-assisterat innehållsskapande per kanal",
              "Automatisk publicering till Facebook, Instagram, LinkedIn",
              "Säsongsanpassade ämnesförslag",
              "Bildgenerering och bildimport från egen webbplats"
            ],
            "provider": { "@type": "Organization", "name": "Lomoolo", "url": "https://lomoolo.com" }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Lomoolo Social",
            "url": "https://lomoolo.com",
            "inLanguage": "sv-SE",
            "publisher": { "@type": "Organization", "name": "Lomoolo", "logo": "https://lomoolo.com/logo.png" },
            "potentialAction": {
              "@type": "SearchAction",
              "target": { "@type": "EntryPoint", "urlTemplate": "https://lomoolo.com/blogg?q={search_term_string}" },
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Lomoolo",
            "url": "https://lomoolo.com",
            "logo": "https://lomoolo.com/logo.png",
            "sameAs": ["https://www.facebook.com/lomoolo", "https://www.instagram.com/lomoolo", "https://www.linkedin.com/company/lomoolo"],
            "contactPoint": { "@type": "ContactPoint", "email": "hej@lomoolo.com", "contactType": "customer service", "availableLanguage": ["Swedish", "English"] }
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Se hur Lomoolo Social fungerar — från tom kalender till ett år av innehåll",
            "description": "Demovideo som visar hela flödet i Lomoolo Social: webbanalys, AI-genererad strategi, 52 veckors innehållsplan, AI-utkast och automatisk publicering till Facebook, Instagram och LinkedIn.",
            "thumbnailUrl": ["https://lomoolo.com/og-image.png"],
            "uploadDate": "2025-01-15",
            "duration": "PT5M",
            "contentUrl": "https://lomoolo.com/",
            "embedUrl": "https://lomoolo.com/#video-showcase",
            "publisher": { "@type": "Organization", "name": "Lomoolo", "logo": { "@type": "ImageObject", "url": "https://lomoolo.com/logo.png" } }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "Vad är Lomoolo Social?", "acceptedAnswer": { "@type": "Answer", "text": "Lomoolo Social är ett AI-drivet verktyg för sociala medier som hjälper svenska småföretag att automatiskt planera, skapa och publicera innehåll till Facebook, Instagram och LinkedIn." } },
              { "@type": "Question", "name": "Behöver jag betalkort för att testa?", "acceptedAnswer": { "@type": "Answer", "text": "Nej. Du testar gratis i 14 dagar utan att ange betaluppgifter." } },
              { "@type": "Question", "name": "Vad kostar Lomoolo Social?", "acceptedAnswer": { "@type": "Answer", "text": "Tre prisplaner: Starter 599 kr/mån, Growth 899 kr/mån och Pro 1 199 kr/mån. Alla planer inkluderar 14 dagars gratis provperiod." } },
              { "@type": "Question", "name": "Vilka sociala medier stöds?", "acceptedAnswer": { "@type": "Answer", "text": "Lomoolo Social stödjer publicering till Facebook, Instagram och LinkedIn. Fler plattformar planeras." } },
              { "@type": "Question", "name": "Hur fungerar AI-texterna?", "acceptedAnswer": { "@type": "Answer", "text": "AI:n skriver ett första utkast baserat på ditt ämne, din bransch och din tonalitet. Du redigerar och gör det till ditt." } },
              { "@type": "Question", "name": "Passar Lomoolo Social min bransch?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. AI:n analyserar din webbplats och anpassar ämnesförslag till just din bransch — oavsett om du driver kafé, redovisningsbyrå eller e-handel." } },
              { "@type": "Question", "name": "Kan jag avbryta när som helst?", "acceptedAnswer": { "@type": "Answer", "text": "Ja. Inga bindningstider. Avsluta din plan med ett klick." } }
            ]
          }
        ]}
      />

      {/* ── 00 NAVBAR ──
          Två varianter renderas:
          1) Default (statisk, längst upp): vit bg, scrollar bort med hero.
          2) Scrolled (fixed): glider in från toppen när hero lämnat viewporten. */}
      {(["default", "scrolled"] as const).map((variant) => {
        const isFixed = variant === "scrolled";
        const visible = isFixed ? scrolled : true;
        return (
          <nav
            key={variant}
            aria-label="Huvudnavigering"
            aria-hidden={isFixed && !scrolled ? true : undefined}
            className={
              isFixed
                ? `fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-all duration-200 ${
                    visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
                  }`
                : "relative z-40 bg-white/95 backdrop-blur shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
            }
          >
            <div className="relative max-w-7xl mx-auto flex h-16 items-center justify-between px-5">
              <Link to="/" className="flex items-center gap-2.5">
                <LomooloLogo size="sm" />
              </Link>

              <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
                <button
                  onClick={() => scrollTo("funktioner")}
                  className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors"
                >
                  Funktioner
                </button>
                <button
                  onClick={() => scrollTo("sa-funkar-det")}
                  className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors"
                >
                  Så funkar det
                </button>
                <button
                  onClick={() => scrollTo("priser")}
                  className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors"
                >
                  Priser
                </button>
                <Link to="/blogg" className="text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">
                  Blogg
                </Link>
              </div>

              <div className="hidden md:flex items-center gap-4">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-v3-body hover:text-v3-heading transition-colors">
                  <LogIn className="h-4 w-4" aria-hidden="true" />
                  Logga in
                </Link>
                {isFixed && (
                  <CoralButton to="/signup">
                    Kom igång gratis <ArrowRight className="h-4 w-4" />
                  </CoralButton>
                )}
              </div>

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
                aria-expanded={mobileMenuOpen}
                tabIndex={isFixed && !scrolled ? -1 : 0}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Mobilmeny renderas endast i den header som är aktiv */}
            {mobileMenuOpen && isFixed === scrolled && (
              <div className="md:hidden border-t bg-white px-5 py-4 space-y-3">
                <button onClick={() => scrollTo("funktioner")} className="block w-full text-left text-sm font-medium py-2">
                  Funktioner
                </button>
                <button
                  onClick={() => scrollTo("sa-funkar-det")}
                  className="block w-full text-left text-sm font-medium py-2"
                >
                  Så funkar det
                </button>
                <button onClick={() => scrollTo("priser")} className="block w-full text-left text-sm font-medium py-2">
                  Priser
                </button>
                <Link to="/blogg" className="block text-sm font-medium py-2">
                  Blogg
                </Link>
                <Link to="/login" className="block text-sm font-medium py-2">
                  Logga in
                </Link>
                <CoralButton to="/signup">
                  Kom igång gratis <ArrowRight className="h-4 w-4" />
                </CoralButton>
              </div>
            )}
          </nav>
        );
      })}

      <main>
        {/* ── 01 HERO V2 ── */}
        <section className="pt-8 pb-8 md:pt-12 md:pb-12 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
          <div className="max-w-3xl mx-auto text-center">
            {/* LCP-elements: rendered without entrance animation so static HTML paints them immediately */}
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-v3-teal/20 bg-v3-teal/10 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-v3-teal" aria-hidden="true" />
              <span className="text-xs font-medium text-v3-teal">AI-driven innehållsplanering för småföretag</span>
            </div>

            <h1
              className="font-jakarta font-bold text-[28px] sm:text-[36px] md:text-[48px] leading-[1.2] text-v3-heading mb-6"
            >
              Du slipper stressen.
              <br />
              <span className="text-v3-coral">LOMOOLO</span> sköter dina sociala medier på autopilot.
            </h1>

            <p
              className="text-base text-v3-body leading-[1.7] max-w-[700px] mx-auto mb-8"
            >
              Lomoolo är verktyget för dig som driver eget och vet att du borde finnas på LinkedIn, Facebook och
              Instagram — men aldrig hinner, orkar eller vet vad du ska skriva. AI hjälper dig hela vägen. Du bestämmer
              alltid vad som går ut.
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4"
            >
              <CoralButton to="/signup">
                Kom igång gratis <ArrowRight className="h-4 w-4" />
              </CoralButton>
                <OutlineButton onClick={() => scrollTo("video-showcase")}>Se hur det funkar</OutlineButton>
            </div>

            <p className="text-xs text-v3-light">
              14 dagar gratis · Inga bindningstider · Avbryt när som helst
            </p>
          </div>

          {/* Hero-mockups — visar 6 exempelinlägg för direkt igenkänning.
              CSS-baserad fade-in (ersätter tidigare framer motion.div). */}
          <div
            data-reveal="fade-up"
            data-revealed="true"
            style={{ transitionDuration: "0.8s", transitionDelay: "0.5s" }}
            className="max-w-5xl mx-auto mt-12 relative z-10"
          >
            <HeroMockups />
          </div>

          <p className="text-center text-[13px] text-v3-light max-w-2xl mx-auto mt-12">
            Byggd av en svensk grundare med 15+ års erfarenhet av att hjälpa småföretag synas online.
          </p>
        </section>

        {/* Sentinel: när detta element lämnar viewporten aktiveras fixed-headern */}
        <div ref={heroSentinelRef} aria-hidden="true" />

        {/* ── 03 SOCIAL PROOF LOGOS [NYTT] ── */}
        {SHOW_SOCIAL_PROOF_LOGOS && (
          <section className="py-12 px-5 bg-neutral-50" aria-label="Företag som använder Lomoolo">
            <div className="max-w-5xl mx-auto text-center">
              <p className="text-xs uppercase tracking-[2px] text-slate-500 mb-6">
                Företag som redan låter Lomoolo sköta jobbet
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                <div className="font-jakarta font-bold text-lg text-slate-600">Holmqvists Frisör</div>
                <div className="font-jakarta font-bold italic text-lg text-slate-600">Nordrevision</div>
                <div className="font-dm font-light tracking-[0.2em] text-lg text-slate-600 hidden sm:block">
                  FORM STUDIO
                </div>
                <div className="font-jakarta font-medium text-lg text-slate-600">Lerumshantverk</div>
                <div className="font-dm font-bold text-lg text-slate-600 hidden sm:block">Café Brännbollen</div>
              </div>
            </div>
          </section>
        )}

        {/* ── 04 PAIN POINTS ── */}
        <section className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <SectionLabel>Känner du igen dig?</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">
                Ambitionen finns. Tiden gör det inte.
              </h2>
              <p className="text-[15px] text-v3-body mb-12 max-w-xl">
                De flesta småföretag vet att de borde synas online. Men verkligheten ser annorlunda ut.
              </p>
            </ScrollReveal>

            <StaggerContainer className="grid md:grid-cols-3 gap-5" staggerDelay={0.12}>
              {[
                {
                  icon: HelpCircle,
                  title: '"Vad ska jag skriva om?"',
                  text: "Du vill posta varje vecka men vet aldrig vad. Inspirationen tar slut och idéerna känns uttjatade.",
                },
                {
                  icon: Clock,
                  title: "Stressen när det är dags",
                  text: "Inget förberett, allt annat prioriteras. Tiden för innehåll finns aldrig och det blir sällan gjort.",
                },
                {
                  icon: Ghost,
                  title: "Det övergivna kontot",
                  text: "Senaste inlägget? Sex månader sedan. Du vet att det ser dåligt ut men orkar inte ta tag i det.",
                },
              ].map((c) => (
                <StaggerItem key={c.title}>
                  <div className="bg-v3-card rounded-xl p-6 shadow-[0_4px_20px_rgba(27,58,75,0.06)] hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(27,58,75,0.1)] transition-all duration-300">
                    <c.icon className="h-7 w-7 text-v3-teal mb-4" aria-hidden="true" />
                    <h3 className="font-jakarta font-bold text-base text-v3-heading mb-2">{c.title}</h3>
                    <p className="text-sm text-v3-body leading-relaxed">{c.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            <ScrollReveal delay={0.3}>
              <p className="text-center text-[15px] font-medium text-v3-body mt-10">Det är därför vi byggde Lomoolo.</p>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 06 VIDEO SHOWCASE — animerad verktygsmockup ── */}
        <section id="video-showcase" className="py-20 md:py-28 px-5 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <ScrollReveal>
              <SectionLabel>Se produkten</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[40px] leading-[1.15] text-v3-heading mb-4">
                Se hur du går från en tom kalender, <br />
                till ett år av innehåll. På fem minuter.
              </h2>
              <p className="text-[15px] md:text-base text-v3-body leading-[1.7] max-w-[680px] mx-auto mb-10">
                Att gå från en tom kalender till ett år av innehåll är enkelt. Fyll i din webbadress och Lomoolo analyserar ditt företag, dina produkter/tjänster, din målgrupp, din ton och tar fram en strategi för vad du ska skriva om och varför. Sedan fylls din kalender med 52 veckor av innehåll som du med hjälp av AI skapar. Inläggen publiceras sedan automatiskt.
              </p>
            </ScrollReveal>

            <ScrollReveal variant="scale-in" delay={0.15}>
              <Suspense fallback={<div className="aspect-[16/10] max-w-4xl mx-auto rounded-2xl bg-v3-card/40" />}>
                <HeroAnimation />
              </Suspense>

              <p className="text-xs md:text-sm text-v3-light mt-6">
                5 min · Visar hela flödet från domän till publicerat inlägg
              </p>
              <div className="mt-6">
                <CoralButton to="/signup">
                  Kom igång gratis <ArrowRight className="h-4 w-4" />
                </CoralButton>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 07 SÅ FUNKAR DET ── */}
        <section
          id="sa-funkar-det"
          className="py-20 px-5 md:py-[40px]"
          style={{ background: "var(--color-v3-bg-primary)" }}
        >
          <div className="max-w-5xl mx-auto text-center">
            <ScrollReveal>
              <SectionLabel>Så funkar det</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[34px] text-v3-heading mb-3">
                Fyra steg. En eftermiddag. Ett helt år.
              </h2>
              <p className="text-[15px] text-v3-body mb-12 max-w-xl mx-auto">
                Två steg gör du en gång. Ett sköter sig självt. Ett tar några timmar i månaden. Det är hela jobbet.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 08a GRUNDA ── */}
        <section id="funktioner" className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal variant="slide-left">
              <SectionLabel>Grunda</SectionLabel>
              <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">
                Skriv in din domän. LOMOOLO förstår resten.
              </h2>
              <p className="text-sm text-v3-body leading-[1.7]">
                Skriv in din webbadress så analyserar LOMOOLO ditt företag, dina produkter, din målgrupp och din
                tonalitet. På sekunder har LOMOOLO en komplett bild av vem du är och vad du borde posta. Ingen
                webbplats? Inga problem. Bygg din profil genom vår guidade wizard istället.
              </p>
            </ScrollReveal>
            <ScrollReveal variant="slide-right" delay={0.2}>
              <DomainMockup />
            </ScrollReveal>
          </div>
        </section>

        {/* ── 08b PLANERA ── */}
        <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal variant="slide-left" delay={0.1}>
              <WeekPlanMockup />
            </ScrollReveal>
            <ScrollReveal variant="slide-right">
              <SectionLabel>Planera</SectionLabel>
              <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">
                52 veckor. Planerade på en eftermiddag.
              </h2>
              <p className="text-sm text-v3-body leading-[1.7]">
                Innan Lomoolo föreslår ett enda ämne skapar det en innehållsstrategi för ditt företag — vad du ska
                kommunicera, i vilken ton och varför det spelar roll för just dig. Strategin lägger grunden. Utifrån den
                fylls årsplanen med 60+ konkreta ämnesidéer anpassade för din bransch, dina produkter och dina mål.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 08c SKAPA ── */}
        <section className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal variant="slide-left">
              <SectionLabel>Skapa</SectionLabel>
              <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">
                AI skriver. Du gör det till ditt.
              </h2>
              <p className="text-sm text-v3-body leading-[1.7] whitespace-pre-line">
                Det är här du lägger din tid - och det är mindre än du tror.{"\n\n"}Välj ett ämne och bestäm hur mycket hjälp du vill ha. Låt AI skriva ett komplett utkast — anpassat efter din ton och för varje kanal. Justera tonen och gör det till ditt eget på några minuter. Generera matchande bilder med AI, importera från din webbplats eller ladda upp egna. En till två timmar och hela nästa månads innehåll är klart.
              </p>
            </ScrollReveal>
            <ScrollReveal variant="slide-right" delay={0.2}>
              <AIWriteMockup />
            </ScrollReveal>
          </div>
        </section>

        {/* ── 08d PUBLICERA ── */}
        <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center [&>*]:min-w-0">

            <ScrollReveal variant="slide-left" delay={0.1}>
              <PublishMockup />
            </ScrollReveal>
            <ScrollReveal variant="slide-right">
              <SectionLabel>Publicera</SectionLabel>
              <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading mb-4">
                Schemalägg. Luta dig tillbaka. Klart.
              </h2>
              <p className="text-sm text-v3-body leading-[1.7] mb-4">
                Datum och tid är redan satt i din årsplan. När inlägget är klart publicerar Lomoolo automatiskt. Vill du
                flytta ett inlägg eller byta datum gör du det med ett klick. Full överblick över vad som är schemalagt,
                vad som publicerats — utan att logga in på varje plattform.
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-v3-body">
                <Facebook className="h-5 w-5 text-[#1877F2]" aria-hidden="true" />
                <Instagram className="h-5 w-5 text-[#E4405F]" aria-hidden="true" />
                <Linkedin className="h-5 w-5 text-[#0A66C2]" aria-hidden="true" />
                <span>Publicera direkt till Facebook, Instagram och LinkedIn</span>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 09 DU BESTÄMMER ALLTID ── */}
        <section className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <SectionLabel>Du bestämmer alltid</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">
                AI föreslår. Du bestämmer. Varje gång.
              </h2>
              <p className="text-[15px] text-v3-body leading-[1.7] mb-8">
                Lomoolo är inte en svart låda som producerar inlägg du inte kan påverka. Det är ett verktyg som jobbar
                med dig — på dina villkor. Varje AI-genererat inlägg kan du redigera, skriva om eller byta ut helt.
                Varje ämne i planen kan du lägga till, ta bort eller flytta. Varje bild kan du byta mot en egen —
                importera direkt från din webbplats eller ladda upp från din dator. Du väljer hur mycket hjälp du vill
                ha. Varje gång.
              </p>
            </ScrollReveal>
            <StaggerContainer className="max-w-xl mx-auto space-y-3 text-left" staggerDelay={0.1}>
              {[
                "Ändra, ta bort eller lägg till precis vad du vill",
                "Skriv själv eller låt AI göra utkastet — du väljer",
                "Det låter som du — för du har alltid sista ordet",
                "Träna AI:n på dina egna inlägg — så blir framtida texter ännu mer som du",
              ].map((t) => (
                <StaggerItem key={t}>
                  <div className="flex items-start gap-3 text-sm text-v3-body">
                    <CheckCircle2 className="h-5 w-5 text-v3-teal mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{t}</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ── 10 COMPARISON + PRICE ── */}
        <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading text-center mb-3">
                Mer än en schemaläggare
              </h2>
              <p className="text-[15px] text-v3-body text-center mb-10 max-w-lg mx-auto">
                Lomoolo gör det som schemaläggare inte kan — hjälper dig veta vad du ska posta, inte bara när.
              </p>
            </ScrollReveal>

            <ScrollReveal variant="scale-in" delay={0.1}>
              <div className="overflow-x-auto mb-16">
                <table className="w-full text-sm" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="text-left py-3 px-4 font-medium text-v3-light">
                        Funktion
                      </th>
                      <th scope="col" className="text-center py-3 px-4 font-medium text-v3-light">
                        Vanliga verktyg
                      </th>
                      <th
                        scope="col"
                        className="text-center py-3 px-4 font-bold text-v3-heading bg-v3-teal/5 rounded-t-lg"
                      >
                        LOMOOLO
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-v3-border-light">
                    {[
                      { f: "Schemalägga inlägg", o: true, l: true },
                      { f: "Publicera automatiskt", o: true, l: true },
                      { f: "AI-skriven text", o: "Vissa", l: true },
                      { f: "AI-genererade bilder", o: false, l: true },
                      { f: "AI:n lär sig din röst från dina egna inlägg", o: false, l: true },
                      { f: "Ämnesförslag per bransch", o: false, l: true },
                      { f: "Komplett årsplan", o: false, l: true },
                      { f: "Innehållsstrategi som grund", o: false, l: true },
                      { f: "Anpassat för svensk marknad", o: false, l: true },
                    ].map((r) => (
                      <tr key={r.f}>
                        <td className="py-3 px-4 text-v3-heading">{r.f}</td>
                        <td className="py-3 px-4 text-center">
                          {r.o === true ? (
                            <>
                              <CheckCircle2 className="h-5 w-5 text-v3-teal mx-auto" aria-hidden="true" />
                              <span className="sr-only">Ja</span>
                            </>
                          ) : r.o ? (
                            <span className="text-v3-light text-xs">{r.o}</span>
                          ) : (
                            <>
                              <X className="h-5 w-5 text-v3-border mx-auto" aria-hidden="true" />
                              <span className="sr-only">Nej</span>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center bg-v3-teal/5">
                          <>
                            <CheckCircle2 className="h-5 w-5 text-v3-teal mx-auto" aria-hidden="true" />
                            <span className="sr-only">Ja</span>
                          </>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <h2 className="font-jakarta font-bold text-[24px] md:text-[28px] text-v3-heading text-center mb-8">
                Professionell marknadsföring — utan byråprislappen
              </h2>
            </ScrollReveal>
            <StaggerContainer className="max-w-2xl mx-auto space-y-3" staggerDelay={0.1}>
              <StaggerItem>
                <div className="flex items-center justify-between p-5 rounded-xl bg-white border">
                  <span className="text-v3-heading">Social media-byrå</span>
                  <span className="text-v3-body font-medium">8 000 – 15 000 kr/mån</span>
                </div>
              </StaggerItem>
              <StaggerItem>
                <div className="flex items-center justify-between p-5 rounded-xl bg-white border">
                  <span className="text-v3-heading">Frilansande innehållsskapare</span>
                  <span className="text-v3-body font-medium">3 000 – 8 000 kr/mån</span>
                </div>
              </StaggerItem>
              <StaggerItem>
                <div className="flex items-center justify-between p-5 rounded-xl bg-v3-coral/10 border border-v3-coral/30">
                  <div className="flex items-center gap-3">
                    <span className="text-v3-heading font-semibold">LOMOOLO</span>
                    <Facebook className="h-4 w-4 text-[#1877F2]" aria-hidden="true" />
                    <Instagram className="h-4 w-4 text-[#E4405F]" aria-hidden="true" />
                    <Linkedin className="h-4 w-4 text-[#0A66C2]" aria-hidden="true" />
                  </div>
                  <span className="text-v3-coral font-bold text-lg">från 599 kr/mån</span>
                </div>
              </StaggerItem>
            </StaggerContainer>
            <ScrollReveal delay={0.3}>
              <p className="text-center text-v3-body text-[15px] mt-6">
                Samma resultat. En bråkdel av kostnaden. Och du behåller kontrollen.
              </p>
              <div className="text-center mt-8">
                {/* CTA-konsistens: "Kom igång gratis" på hero/header/prisjämförelse/final */}
                <CoralButton to="/signup">
                  Kom igång gratis <ArrowRight className="h-4 w-4" />
                </CoralButton>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 11 CUSTOMER STORIES V2 (3 stories) ── */}
        <section className="bg-v3-dark py-20 md:py-28 px-5">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal>
              <p className="text-[11px] uppercase tracking-[2px] font-semibold text-white/50 mb-4 text-center">
                Från tom kalender till ett helt år
              </p>
              <h2 className="font-jakarta font-bold text-[26px] md:text-[34px] text-white mb-3 text-center">
                Tre företag. Samma resultat.
              </h2>
              <p className="text-[15px] text-white/70 leading-[1.7] mb-12 max-w-2xl mx-auto text-center">
                Olika branscher, olika utmaningar — samma berättelse: tid sparad, synlighet vunnen.
              </p>
            </ScrollReveal>

            <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" staggerDelay={0.12}>
              {[
                {
                  title: "Erik planerade ett helt år på 45 minuter.",
                  story:
                    "Erik driver en IT-konsultfirma i Stockholm. Han visste att han borde synas på LinkedIn — hans kunder googlar alltid på honom innan de skriver ett avtal. Men varje gång han satte sig för att skriva ett inlägg hade han tio andra saker som var viktigare. Med Lomoolo fyllde han en hel årsplan med ämnen anpassade för hans bransch. Sen lät han AI skriva ett första utkast för varje inlägg, justerade med sin egen röst och schemalade. Nu vet han varje måndag vad som ska gå ut den veckan. Utan stress. Utan gissningar.",
                  stats: [
                    { n: "45 min", s: "att planera\nett helt år" },
                    { n: "1 tim", s: "per månad för\nallt innehåll" },
                    { n: "0", s: "missade veckor" },
                  ],
                },
                {
                  title: "Anna fick tillbaka söndagskvällarna.",
                  story:
                    'Anna driver en mindre redovisningsbyrå i Göteborg. I flera år hade hon "skriva LinkedIn-inlägg" på söndagskvällens to-do-lista — och i flera år sköt hon upp det. Hon ville framstå som kunnig och uppdaterad, men varje söndag stirrade hon på en tom skärm. Med Lomoolo fyllde hon årsplanen med ämnen som faktiskt rör hennes kunder: deklaration, K10, fåmansbolag, momsregler. AI:n förstår hennes ton — saklig, varm, pedagogisk. Nu publiceras något varje vecka. Söndagskvällar tillhör familjen igen.',
                  stats: [
                    { n: "52", s: "inlägg\nper år" },
                    { n: "0", s: "söndagskvällar\noffrade" },
                    { n: "+38%", s: "fler nya\nkundförfrågningar" },
                  ],
                },
                {
                  title: "Marcus tog kontoret från tyst till synligt.",
                  story:
                    "Marcus driver en arkitektbyrå i Malmö. Hans Instagram hade legat dött i åtta månader när han hittade Lomoolo. Inte för att han inte hade material — han har snygga projektbilder från varje uppdrag — utan för att han aldrig hann skriva texterna. Lomoolo löste det: han laddar upp bilden, AI:n föreslår tre olika texter, han väljer en, eventuellt redigerar. Tre minuter per inlägg. Idag publicerar han varje vecka. Hans nya kunder nämner Instagram i första mötet.",
                  stats: [
                    { n: "3 min", s: "per inlägg\nfrån bild till klar" },
                    { n: "1 / vecka", s: "inlägg\nfrån noll" },
                    { n: "6", s: "nya uppdrag\nförsta halvåret" },
                  ],
                },
              ].map((c) => (
                <StaggerItem key={c.title}>
                  <div className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-7 h-full flex flex-col shadow-[0_8px_32px_rgba(0,0,0,0.25)] hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] transition-all duration-300">
                    <h3 className="font-jakarta font-bold text-[20px] leading-[1.3] text-white mb-4">
                      {c.title}
                    </h3>
                    <p className="text-sm text-white/70 leading-[1.7] mb-6 flex-1">{c.story}</p>
                    <div className="flex gap-6 pt-4 border-t border-white/10">
                      {c.stats.map((s) => (
                        <div key={s.n}>
                          <div className="font-jakarta font-bold text-[22px] text-v3-coral leading-none">{s.n}</div>
                          <div className="text-[11px] text-white/50 whitespace-pre-line mt-1 leading-tight">{s.s}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ── 12 TESTIMONIAL BLOCK [NYTT] ── */}
        {SHOW_TESTIMONIALS && (
          <section className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
            <div className="max-w-6xl mx-auto">
              <ScrollReveal>
                <SectionLabel>Vad kunder säger</SectionLabel>
                <h2 className="font-jakarta font-bold text-[28px] md:text-[34px] text-v3-heading text-center mb-3">
                  Det de inte trodde de skulle behöva.
                </h2>
                <p className="text-[15px] text-v3-body leading-[1.7] max-w-xl mx-auto text-center mb-12">
                  Tre röster från företag som testat Lomoolo i minst en månad.
                </p>
              </ScrollReveal>

              <StaggerContainer className="grid md:grid-cols-3 gap-5" staggerDelay={0.12}>
                {[
                  {
                    quote:
                      "Jag hade testat tre andra verktyg innan. Alla var schemaläggare som krävde att jag redan hade innehåll. Lomoolo var det första som gav mig något att schemalägga.",
                    initials: "AL",
                    name: "Anna L.",
                    company: "Nordrevision, Göteborg",
                    avatarBg: "bg-v3-coral",
                    avatarText: "text-white",
                  },
                  {
                    quote:
                      "Det jag underskattade var hur skönt det är att veta vad som ska gå ut. Inte bara att det går ut — utan att veta att det är genomtänkt och passar mitt varumärke.",
                    initials: "MK",
                    name: "Marcus K.",
                    company: "Form Studio, Malmö",
                    avatarBg: "bg-v3-dark",
                    avatarText: "text-white",
                  },
                  {
                    quote:
                      "Jag är inte tekniskt lagd. Det enda jag gjorde var att skriva in min webbadress. Två veckor senare publicerar Lomoolo åt mig medan jag faktiskt jobbar med mina kunder.",
                    initials: "KH",
                    name: "Karin H.",
                    company: "Holmqvists Frisör, Lerum",
                    avatarBg: "bg-v3-card",
                    avatarText: "text-v3-heading",
                  },
                ].map((t) => (
                  <StaggerItem key={t.name}>
                    <figure className="bg-white rounded-2xl p-7 shadow-[0_6px_24px_rgba(27,58,75,0.08)] border border-v3-border-light h-full flex flex-col hover:-translate-y-1 hover:shadow-[0_10px_32px_rgba(27,58,75,0.12)] transition-all duration-300">
                      <Quote className="h-8 w-8 text-v3-coral mb-4 shrink-0" aria-hidden="true" />
                      <blockquote className="text-[15px] text-v3-heading leading-[1.6] flex-1 mb-6">
                        "{t.quote}"
                      </blockquote>
                      <figcaption className="flex items-center gap-3 pt-4 border-t border-v3-border-light">
                        <div
                          className={`w-10 h-10 rounded-full ${t.avatarBg} ${t.avatarText} flex items-center justify-center font-jakarta font-bold text-sm`}
                          role="img"
                          aria-label={`Profilbild för ${t.name}`}
                        >
                          {t.initials}
                        </div>
                        <div>
                          <div className="font-jakarta font-semibold text-sm text-v3-heading leading-tight">
                            {t.name}
                          </div>
                          <div className="text-xs text-v3-light">{t.company}</div>
                        </div>
                      </figcaption>
                    </figure>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </div>
          </section>
        )}

        {/* ── 13 PERSONA CARDS ── */}
        <section className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <SectionLabel>Vem är LOMOOLO för?</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">
                Du som har bättre saker att göra.
              </h2>
              <p className="text-[15px] text-v3-body mb-12">
                Lomoolo är byggt för dig som vill synas — utan att det tar över din vardag.
              </p>
            </ScrollReveal>

            <StaggerContainer className="grid md:grid-cols-3 gap-5" staggerDelay={0.12}>
              {[
                {
                  icon: Building2,
                  title: "Småföretagaren",
                  text: "Du vet att synlighet online är viktigt, men det finns alltid annat som prioriteras före marknadsföring. Lomoolo gör det till den enklaste delen av din vecka.",
                },
                {
                  icon: TrendingUp,
                  title: "Den ambitiösa",
                  text: "Du saknar redaktionellt stöd och har inte råd med en byrå — men vill ändå ha professionellt innehåll. Lomoolo ger dig en hel content-avdelning i en app.",
                },
                {
                  icon: Zap,
                  title: "Tidspressaren",
                  text: "Du vill bygga ett starkt varumärke online utan att det tar över din vardag. Lomoolo ger dig tillbaka den tid du inte visste att du hade.",
                },
              ].map((c) => (
                <StaggerItem key={c.title}>
                  <div className="bg-v3-card rounded-xl p-6 text-center shadow-[0_4px_20px_rgba(27,58,75,0.06)] hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(27,58,75,0.1)] transition-all duration-300">
                    <c.icon className="h-7 w-7 text-v3-teal mx-auto mb-4" aria-hidden="true" />
                    <h3 className="font-jakarta font-bold text-base text-v3-heading mb-2">{c.title}</h3>
                    <p className="text-sm text-v3-body leading-relaxed">{c.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ── 14 PRICING V2 ── */}
        <section id="priser" className="py-20 md:py-28 px-5" style={{ background: "var(--color-v3-bg-primary)" }}>
          <div className="max-w-5xl mx-auto text-center">
            <ScrollReveal>
              <SectionLabel>Priser</SectionLabel>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading mb-3">
                Välj den plan som passar dig.
              </h2>
              {/* TODO: Uppdatera subline när trial-modell beslutats (CC-free vs CC-required) */}
              <p className="text-[15px] text-v3-body mb-12">
                Alla planer inkluderar 14 dagars gratis provperiod. Inget betalkort krävs.
              </p>
            </ScrollReveal>

            <StaggerContainer className="grid md:grid-cols-3 gap-5 items-start" staggerDelay={0.12}>
              <StaggerItem>
                <div className="rounded-2xl border border-v3-border bg-[#FFFAF5] p-8 text-left shadow-[0_6px_24px_rgba(27,58,75,0.06)] hover:-translate-y-1 hover:shadow-[0_10px_32px_rgba(27,58,75,0.1)] transition-all duration-300">
                  <h3 className="font-jakarta font-bold text-xl text-v3-heading mb-1">Starter</h3>
                  <div className="mb-1">
                    <span className="font-jakarta font-bold text-4xl text-v3-coral">599</span>{" "}
                    <span className="text-sm text-v3-light">kr/mån</span>
                  </div>
                  <p className="text-sm text-v3-body mb-6">Perfekt för att komma igång.</p>
                  <ul className="space-y-3 mb-8 text-sm text-v3-body">
                    {[
                      "Djup AI-analys av ditt företag",
                      "Innehållsstrategi som grund",
                      "Årsplan med ämnesförslag",
                      "AI-skriven text",
                      "AI-genererade bilder",
                      "Automatisk publicering",
                      "1 social kanal",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" aria-hidden="true" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    className="block w-full text-center rounded-[10px] border border-v3-heading/20 py-3 text-sm font-semibold text-v3-heading hover:bg-v3-heading/5 transition-colors"
                  >
                    Testa gratis i 14 dagar
                  </Link>
                  <p className="text-xs text-v3-light text-center mt-3">Inga bindningstider. Avbryt när som helst.</p>
                </div>
              </StaggerItem>

              <StaggerItem>
                <div className="rounded-2xl bg-v3-dark p-8 text-left text-white relative md:scale-[1.03] shadow-[0_12px_40px_rgba(0,0,0,0.35)] hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.45)] transition-all duration-300">
                  <span className="absolute -top-3 right-6 bg-v3-coral text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Populärast
                  </span>
                  <h3 className="font-jakarta font-bold text-xl mb-1 text-white">Growth</h3>
                  <div className="mb-1">
                    <span className="font-jakarta font-bold text-4xl text-v3-coral">899</span>{" "}
                    <span className="text-sm text-white/50">kr/mån</span>
                  </div>
                  <p className="text-sm text-white/60 mb-6">För dig som vill synas på riktigt.</p>
                  <ul className="space-y-3 mb-8 text-sm text-white/70">
                    {[
                      "Allt i Starter",
                      "3 sociala kanaler",
                      "Fler AI-genererade bilder per månad",
                      "Performance insights",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" aria-hidden="true" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    className="block w-full text-center rounded-[10px] bg-v3-coral hover:bg-v3-coral-hover py-3 text-sm font-semibold text-white transition-colors"
                  >
                    Testa gratis i 14 dagar{" "}
                    <span className="ml-1" aria-hidden="true">
                      →
                    </span>
                  </Link>
                  <p className="text-xs text-white/50 text-center mt-3">Inga bindningstider. Avbryt när som helst.</p>
                </div>
              </StaggerItem>

              <StaggerItem>
                <div className="rounded-2xl border border-v3-border bg-[#FFFAF5] p-8 text-left shadow-[0_6px_24px_rgba(27,58,75,0.06)] hover:-translate-y-1 hover:shadow-[0_10px_32px_rgba(27,58,75,0.1)] transition-all duration-300">
                  <h3 className="font-jakarta font-bold text-xl text-v3-heading mb-1">Pro</h3>
                  <div className="mb-1">
                    <span className="font-jakarta font-bold text-4xl text-v3-coral">1 199</span>{" "}
                    <span className="text-sm text-v3-light">kr/mån</span>
                  </div>
                  <p className="text-sm text-v3-body mb-6">
                    För dig som vill maxa er närvaro på sociala medier med extra kraft och teamfunktioner.
                  </p>
                  <ul className="space-y-3 mb-8 text-sm text-v3-body">
                    {["Allt i Growth", "Obegränsade AI-genererade bilder", "Teamfunktioner", "Prioriterad support"].map(
                      (f) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-v3-teal mt-0.5 shrink-0" aria-hidden="true" /> {f}
                        </li>
                      ),
                    )}
                  </ul>
                  <Link
                    to="/signup"
                    className="block w-full text-center rounded-[10px] border border-v3-heading/20 py-3 text-sm font-semibold text-v3-heading hover:bg-v3-heading/5 transition-colors"
                  >
                    Testa gratis i 14 dagar
                  </Link>
                  <p className="text-xs text-v3-light text-center mt-3">Inga bindningstider. Avbryt när som helst.</p>
                </div>
              </StaggerItem>
            </StaggerContainer>

            <ScrollReveal delay={0.3}>
              <p className="text-[13px] text-v3-body mt-8">Spara 2 månader med årsbetalning.</p>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 15 FAQ V2 (6 befintliga + 2 nya) ── */}
        <section className="bg-white py-20 md:py-28 px-5">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-v3-heading text-center mb-10">
                Vanliga frågor
              </h2>
            </ScrollReveal>
            <ScrollReveal variant="fade-up" delay={0.1}>
              <Accordion type="single" collapsible className="space-y-0 divide-y divide-v3-border-light">
                {[
                  {
                    q: "Behöver jag betalkort för att testa?",
                    a: "Nej. Du testar gratis i 14 dagar utan att ange betaluppgifter.",
                  },
                  { q: "Kan jag byta plan senare?", a: "Absolut. Du kan uppgradera eller nedgradera när som helst." },
                  {
                    q: "Vilka sociala medier stöds?",
                    a: "Facebook, Instagram och LinkedIn. Fler plattformar är på väg.",
                  },
                  {
                    q: "Hur fungerar AI-texterna?",
                    a: "AI:n skriver ett första utkast baserat på ditt ämne, din bransch och din tonalitet. Du redigerar och gör det till ditt.",
                  },
                  {
                    q: "Passar LOMOOLO min bransch?",
                    a: "Ja. AI:n analyserar din webbplats och anpassar ämnesförslag till just din bransch — oavsett om du driver kafé, redovisningsbyrå eller e-handel.",
                  },
                  {
                    q: "Kan jag avbryta när som helst?",
                    a: "Ja. Inga bindningstider. Avsluta din plan med ett klick.",
                  },
                  {
                    q: "Vad händer med mitt innehåll om jag säger upp?",
                    a: "Du behåller äganderätten till allt innehåll du skapat — text, bilder, strategi. Du kan exportera allt med ett klick (GDPR-export) innan eller efter uppsägning. Vi raderar din data inom 30 dagar efter uppsägning, om du inte återaktiverar prenumerationen.",
                  },
                  {
                    q: "Är min data säker?",
                    a: "Ja. Lomoolo är byggt på Supabase med svenska/europeiska servrar. All data är krypterad och separerad per kund. Vi följer GDPR och säljer aldrig din data vidare. OAuth-tokens till sociala medier krypteras med AES-256.",
                  },
                ].map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                    <AccordionTrigger className="font-jakarta font-bold text-[18px] text-v3-heading py-5 hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-v3-body pb-5">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollReveal>
          </div>
        </section>

        {/* ── 16 FINAL CTA V2 ── */}
        <section className="bg-v3-dark py-20 md:py-28 px-5">
          <div className="max-w-2xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="font-jakarta font-bold text-[28px] md:text-[32px] text-white mb-4">
                Du har skjutit upp det tillräckligt länge.
              </h2>
              <p className="text-[15px] text-white/65 mb-8 max-w-md mx-auto">
                Om en vecka kan du ha en strategi, en årsplan och dina första inlägg klara. Inte för att du jobbat
                hårdare — utan för att du äntligen har ett system som jobbar med dig.
              </p>
              <CoralButton to="/signup" large>
                Kom igång gratis <ArrowRight className="h-4 w-4" />
              </CoralButton>
              <p className="text-xs text-white/40 mt-4">Inget betalkort krävs. Avbryt när som helst.</p>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <V3Footer />
    </div>
  );
};

/* ─── MOCKUPS (kopior av LandingPageV3-mockups) ─── */

function YearPlanMockupV3() {
  const weeks = [
    {
      week: "V.1",
      date: "2 jan",
      fb: "Så tänker jag kring prisstrategi",
      ig: "Bakom kulisserna: nytt kontor",
      status: "Planerad",
    },
    {
      week: "V.2",
      date: "9 jan",
      fb: "3 frågor du borde ställa din redovisningskonsult",
      ig: "Min morgonrutin innan kundmöten",
      status: "Planerad",
    },
    {
      week: "V.3",
      date: "16 jan",
      fb: "Vad en bra projektledare faktiskt gör",
      ig: "Veckans favoritverktyg",
      status: "Skapad",
    },
    {
      week: "V.4",
      date: "23 jan",
      fb: "Case: så hjälpte vi en kund växa 40%",
      ig: "Vardag på kontoret",
      status: "Skapad",
    },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs md:text-sm">
        <thead>
          <tr className="text-left text-v3-light">
            <th className="py-2 px-3 font-medium">Vecka</th>
            <th className="py-2 px-3 font-medium">Datum</th>
            <th className="py-2 px-3 font-medium">
              <span className="inline-flex items-center gap-1">
                <Facebook className="h-3.5 w-3.5 text-[#1877F2]" aria-hidden="true" /> Facebook
              </span>
            </th>
            <th className="py-2 px-3 font-medium">
              <span className="inline-flex items-center gap-1">
                <Instagram className="h-3.5 w-3.5 text-[#E4405F]" aria-hidden="true" /> Instagram
              </span>
            </th>
            <th className="py-2 px-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.week} className="border-t border-v3-border-light">
              <td className="py-3 px-3 font-medium text-v3-heading">{w.week}</td>
              <td className="py-3 px-3 text-v3-body">{w.date}</td>
              <td className="py-3 px-3 text-v3-heading">{w.fb}</td>
              <td className="py-3 px-3 text-v3-heading">{w.ig}</td>
              <td className="py-3 px-3">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${w.status === "Planerad" ? "bg-v3-muted text-v3-light" : "bg-v3-teal/10 text-v3-teal"}`}
                >
                  {w.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DomainMockup() {
  const results = [
    { key: "Bransch", value: "Redovisning & ekonomi" },
    { key: "Målgrupp", value: "Småföretag som vill ha koll på ekonomin" },
    { key: "Tonalitet", value: "Trygg, kunnig, jordnära" },
    { key: "Ämnesförslag", value: "52 veckor genererade" },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8 shadow-[0_6px_24px_rgba(27,58,75,0.06)]">
      <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-3 mb-5 shadow-sm">
        <div className="w-9 h-9 rounded-full bg-v3-teal flex items-center justify-center">
          <Search className="h-4 w-4 text-white" aria-hidden="true" />
        </div>
        <span className="text-sm font-medium text-v3-heading">nordrevision.se</span>
      </div>
      <div className="space-y-2.5">
        {results.map((r) => (
          <div key={r.key} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
            <div className="w-7 h-7 rounded-full bg-v3-teal/10 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 text-v3-teal" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-v3-light font-semibold">{r.key}</div>
              <div className="text-sm font-semibold text-v3-heading">{r.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekPlanMockup() {
  const weeks = [
    { num: "V.10", topic: "Bakom kulisserna: vårt senaste hemmaprojekt", done: true },
    { num: "V.11", topic: "Tre färgtrender för 2026 — och vad jag tycker", done: true },
    { num: "V.12", topic: "Innan & efter: vardagsrummet i Lerum", done: false },
    { num: "V.13", topic: "Så väljer du rätt belysning till hemmet", done: false },
    { num: "V.14", topic: "Vad kostar en inredningskonsultation egentligen?", done: false },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8 shadow-[0_6px_24px_rgba(27,58,75,0.06)]">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-jakarta font-bold text-base text-v3-heading">Veckoplan</h3>
        <span className="text-xs text-v3-light">Mars 2026</span>
      </div>
      <div className="space-y-2">
        {weeks.map((w) => (
          <div key={w.num} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
            <span className={`text-xs font-bold ${w.done ? "text-v3-teal" : "text-v3-coral"}`}>{w.num}</span>
            <span className="text-sm text-v3-heading flex-1">{w.topic}</span>
            {w.done ? (
              <CheckCircle2 className="h-5 w-5 text-v3-teal" aria-hidden="true" />
            ) : (
              <ArrowRight className="h-4 w-4 text-v3-coral" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AIWriteMockup() {
  return (
    <div className="bg-v3-card rounded-2xl p-8 shadow-[0_6px_24px_rgba(27,58,75,0.06)]">
      <div className="bg-white rounded-xl px-4 py-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-v3-light font-semibold mb-1">Valt ämne</div>
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-v3-coral" aria-hidden="true" />
          <span className="text-sm font-semibold text-v3-heading">
            Tre tecken på att ditt team behöver en utvecklingssamtal
          </span>
        </div>
      </div>
      <div className="bg-white rounded-xl px-4 py-4 border-l-4 border-v3-teal">
        <div className="text-[10px] uppercase tracking-wider text-v3-teal font-semibold mb-2">LOMOOLO föreslår</div>
        <p className="text-sm text-v3-heading leading-relaxed">
          Många chefer skjuter upp utvecklingssamtal tills det blir akut — men då är det ofta för sent. Här är tre
          tidiga signaler att ditt team behöver pratas vid: oklarhet i prioriteringar, sjunkande engagemang i möten, och
          frågor som inte ställs längre. Hör av dig om du vill prata om hur ni kan strukturera det.
        </p>
      </div>
    </div>
  );
}

function PublishMockup() {
  const items = [
    {
      platform: "IG",
      color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
      title: "Klart: takrenovering på villa i Mölndal",
      time: "Imorgon 08:30",
      status: "Schemalagd",
      statusColor: "bg-v3-teal/10 text-v3-teal",
    },
    {
      platform: "FB",
      color: "bg-[#1877F2]",
      title: "Tre tecken på att taket behöver ses över",
      time: "Igår 09:00",
      status: "Publicerad",
      statusColor: "bg-v3-success-light text-v3-success",
    },
    {
      platform: "IN",
      color: "bg-[#0A66C2]",
      title: "Vad kostar en tillbyggnad egentligen?",
      time: "Måndag 10:00",
      status: "Schemalagd",
      statusColor: "bg-v3-teal/10 text-v3-teal",
    },
  ];
  return (
    <div className="bg-v3-card rounded-2xl p-8 space-y-3 shadow-[0_6px_24px_rgba(27,58,75,0.06)]">
      {items.map((item) => (
        <div key={item.title} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center text-white text-xs font-bold shrink-0`}
          >
            {item.platform}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-v3-heading truncate">{item.title}</div>
            <div className="text-xs text-v3-light">{item.time}</div>
          </div>
          <span
            className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${item.statusColor}`}
          >
            {item.status}
          </span>
        </div>
      ))}
    </div>
  );
}

export default HomeV2;
